import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Layout, ConfigProvider, App as AntdApp } from 'antd'
import { useAuthStore, initializeAuth } from '@/store/authStore'

// 引入设计令牌（统一设计系统）
import '@/styles/tokens.css'
// 引入响应式文字样式
import '@/styles/responsive-text.css'
// 引入图片预览样式
import '@/styles/image-preview.css'

// 页面组件
import HomePage from '@/pages/HomePage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import VideoGenerationPage from '@/pages/VideoGenerationPage'
import ImageGenerationPage from '@/pages/ImageGenerationPage'
import HistoryPage from '@/pages/HistoryPage'
import ProfilePage from '@/pages/ProfilePage'
import PaymentPage from '@/pages/PaymentPage'
import PaymentTestPage from '@/pages/PaymentTestPage'
import ReferralPage from '@/pages/ReferralPage'
import AdminDashboard from '@/pages/AdminDashboard'
import AgentQueryPage from '@/pages/AgentQueryPage'
import AgentPortalPage from '@/pages/AgentPortalPage'

// 组件
import Header from '@/components/Layout/Header'
import Footer from '@/components/Layout/Footer'
import MobileBottomNav from '@/components/Layout/MobileBottomNav'
import Sidebar from '@/components/Layout/Sidebar'
import ProtectedRoute from '@/components/Auth/ProtectedRoute'
import LoadingSpinner from '@/components/Common/LoadingSpinner'

const { Content } = Layout

const App: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  // 初始化认证状态
  useEffect(() => {
    initializeAuth()
  }, [])

  // 判断是否是管理后台页面
  const isAdminPage = location.pathname.startsWith('/admin')
  
  // 判断是否显示侧边栏（首页、登录、注册、代理商查询页面不显示）
  const showSidebar = isAuthenticated && 
    !['/login', '/register', '/', '/agent-query'].includes(location.pathname)
  
  // 判断是否显示顶部Header（首页始终显示，其他页面根据登录状态，代理商查询页面不显示）
  const showHeader = (location.pathname === '/' || isAuthenticated) && 
    location.pathname !== '/agent-query'

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#3b82f6',
          colorSuccess: '#10b981',
          colorWarning: '#f59e0b',
          colorError: '#ef4444',
          borderRadius: 8,
          fontSize: 14,
        },
      }}
    >
      <AntdApp>
      {/* 管理后台页面独立布局 */}
      {isAdminPage ? (
        <Routes>
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
      ) : (
        <Layout className="min-h-screen">
          {/* PC端左侧边栏 - 只在特定页面显示 */}
          {showSidebar && <Sidebar />}
          
          {/* 主布局 */}
          <Layout 
            className={`pb-0 md:pb-0 main-app-layout ${!showSidebar ? 'ml-0' : ''}`}
          >
            {/* 顶部Header - 首页始终显示 */}
            {showHeader && <Header />}
            <Content className="flex-1">
              <div className={showSidebar ? 'container-max' : ''}>
              <Routes>
                {/* 公开路由 */}
                <Route path="/" element={<HomePage />} />
                <Route path="/agent-query" element={<AgentQueryPage />} />
                <Route 
                  path="/login" 
                  element={
                    isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
                  } 
                />
                <Route 
                  path="/register" 
                  element={
                    isAuthenticated ? <Navigate to="/" replace /> : <RegisterPage />
                  } 
                />

                {/* 受保护的路由 */}
                <Route
                  path="/video"
                  element={
                    <ProtectedRoute>
                      <VideoGenerationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/image"
                  element={
                    <ProtectedRoute>
                      <ImageGenerationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/history"
                  element={
                    <ProtectedRoute>
                      <HistoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment"
                  element={
                    <ProtectedRoute>
                      <PaymentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/referral"
                  element={
                    <ProtectedRoute>
                      <ReferralPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-test"
                  element={
                    <ProtectedRoute>
                      <PaymentTestPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/agent-portal"
                  element={
                    <ProtectedRoute>
                      <AgentPortalPage />
                    </ProtectedRoute>
                  }
                />

                {/* 404 路由 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </div>
            </Content>
            <Footer />
            {/* 移动端底部导航 - 只在登录状态下显示 */}
            {isAuthenticated && <MobileBottomNav />}
          </Layout>
        </Layout>
      )}
      </AntdApp>
    </ConfigProvider>
  )
}

export default App
